<template>
  <main class="rh-colaborador-page">
    <rh-cabecalho
      pagina="inicio"
      :mostrar-apresentacao="false"
      :unidade-id="unidadeId"
      :empresas="empresas"
      @empresa-selecionada="selecionarEmpresa"
    />

    <b-message v-if="erro" type="is-danger" aria-close-label="Fechar" @close="erro = ''">{{ erro }}</b-message>
    <b-loading :active="loading" :is-full-page="false"></b-loading>

    <section class="welcome">
      <div class="welcome-intro">
        <p class="welcome-context">{{ equipePrincipal ? `Equipe ${equipePrincipal.nome}` : 'Meu espaço' }}<span v-if="equipePrincipal && equipePrincipal.lider"> · Líder: {{ equipePrincipal.lider }}</span></p>
        <h1>Olá, {{ primeiroNome }}<span class="welcome-spark">.</span></h1>
        <p class="welcome-copy">Que bom ter você por aqui. Seu desenvolvimento começa com pequenos passos.</p>
        <p v-if="equipes.length > 1" class="welcome-other-teams">Você participa de {{ equipes.length }} equipes nesta empresa.</p>
      </div>

      <div v-if="cicloDestaque" class="featured-cycle">
        <div class="featured-top">
          <span class="featured-kicker">DIAGNÓSTICO DISPONÍVEL</span>
          <span class="anonymous-pill"><b-icon icon="shield-check" size="is-small" /> Anônimo</span>
        </div>
        <h2>{{ cicloDestaque.nome }}</h2>
        <p>{{ cicloDestaque.tipo === '7d' ? 'Compartilhe sua percepção sobre a liderança.' : 'Compartilhe sua percepção sobre a equipe.' }} Sua participação ajuda a construir um ambiente melhor.</p>
        <div class="featured-meta">
          <span><b-icon icon="account-group-outline" size="is-small" /> {{ cicloDestaque.equipe }}</span>
          <span><b-icon icon="calendar-clock" size="is-small" /> Até {{ dataCurta(cicloDestaque.fimEm) }}</span>
        </div>
        <router-link class="featured-action" :to="destinoCiclo(cicloDestaque)">
          Responder diagnóstico <b-icon icon="arrow-right" size="is-small" />
        </router-link>
      </div>
      <div v-else-if="ciclosDisponiveis" class="featured-cycle featured-cycle-empty">
        <span class="featured-kicker">TUDO EM DIA</span>
        <h2>Nenhum diagnóstico pendente agora.</h2>
        <p>Quando um novo ciclo estiver disponível para você, ele aparecerá aqui.</p>
        <router-link class="featured-secondary" to="/rh/desenvolvimento">Explorar meu desenvolvimento <b-icon icon="arrow-right" size="is-small" /></router-link>
      </div>
    </section>

    <section class="privacy-note" aria-label="Privacidade do diagnóstico">
      <span class="privacy-icon"><b-icon icon="lock-outline" /></span>
      <div>
        <strong>Sua resposta é protegida.</strong>
        <p>Sua liderança vê só a média da equipe, e apenas depois de {{ minimoRespostas ? `${minimoRespostas} respostas` : 'atingir o mínimo de respostas definido para a empresa' }} e do encerramento do ciclo. Nenhuma resposta individual é exibida.</p>
      </div>
    </section>

    <div v-if="!loading && !empresas.length" class="empty-state">
      Nenhuma empresa vinculada ao seu acesso. Peça ao administrador para configurar sua unidade.
    </div>

    <template v-if="unidadeId">
      <section class="development-section">
        <div class="section-heading">
          <div>
            <p class="eyebrow">SEU CAMINHO</p>
            <h2>Meu desenvolvimento</h2>
            <p>Um resumo dos passos que você já deu e do que vem pela frente.</p>
          </div>
          <router-link class="text-link" to="/rh/desenvolvimento">Ver desenvolvimento <b-icon icon="arrow-right" size="is-small" /></router-link>
        </div>
        <div v-if="indicadoresDisponiveis" class="indicator-grid">
          <router-link class="indicator-card" to="/rh/desenvolvimento">
            <span class="indicator-icon violet"><b-icon icon="target" /></span>
            <span class="indicator-value">{{ pdisAtivos }}</span>
            <strong>PDIs em andamento</strong>
            <small>Objetivos para evoluir</small>
          </router-link>
          <router-link class="indicator-card" to="/rh/desenvolvimento">
            <span class="indicator-icon blue"><b-icon icon="account-voice" /></span>
            <span class="indicator-value">{{ proximosEncontros }}</span>
            <strong>Próximos 1:1</strong>
            <small>Conversas agendadas</small>
          </router-link>
          <router-link class="indicator-card" to="/rh/desenvolvimento">
            <span class="indicator-icon peach"><b-icon icon="flag-checkered" /></span>
            <span class="indicator-value">{{ metas.length }}</span>
            <strong>Metas atribuídas</strong>
            <small>Resultados para acompanhar</small>
          </router-link>
          <router-link class="indicator-card" :to="{ name: 'ead-meus-cursos' }">
            <span class="indicator-icon mint"><b-icon icon="school-outline" /></span>
            <span class="indicator-value">{{ cursosConcluidos }}</span>
            <strong>Cursos concluídos</strong>
            <small>Conhecimento conquistado</small>
          </router-link>
        </div>
        <div v-else-if="!loading" class="empty-state">Não foi possível carregar seus indicadores agora.</div>
      </section>

      <section class="cycles-section">
        <div class="section-heading">
          <div>
            <p class="eyebrow">SUA VOZ IMPORTA</p>
            <h2>Ciclos para responder</h2>
            <p>Responda aos diagnósticos abertos para suas equipes.</p>
          </div>
          <span class="cycle-count">{{ ciclos.length }} {{ ciclos.length === 1 ? 'pendente' : 'pendentes' }}</span>
        </div>
        <div v-if="ciclos.length" class="cycle-list">
          <article v-for="ciclo in ciclos" :key="`${ciclo.id}-${ciclo.tipo}`" class="cycle-row">
            <span class="cycle-badge"><b-icon icon="clipboard-text-outline" /></span>
            <div class="cycle-details">
              <strong>{{ ciclo.nome }}</strong>
              <span>{{ ciclo.equipe }} · Até {{ dataCurta(ciclo.fimEm) }}</span>
            </div>
            <router-link class="cycle-link" :to="destinoCiclo(ciclo)">Responder <b-icon icon="arrow-right" size="is-small" /></router-link>
          </article>
        </div>
        <div v-else-if="ciclosDisponiveis && !loading" class="empty-state">Você não tem ciclos pendentes no momento. Obrigado por participar!</div>
        <div v-else-if="!loading" class="empty-state">Não foi possível carregar seus ciclos agora.</div>
      </section>

      <section class="result-section">
        <div class="section-heading">
          <div>
            <p class="eyebrow">SAÚDE DA EQUIPE</p>
            <h2>Último resultado divulgado</h2>
            <p>Médias da equipe em um ciclo que você respondeu.</p>
          </div>
        </div>
        <div v-if="ultimoResultado" class="result-card">
          <div class="result-heading">
            <div>
              <p class="eyebrow">{{ ultimoResultado.nomeCiclo }} · encerrado em {{ dataCurta(ultimoResultado.encerradoEm) }}</p>
              <h3>{{ ultimoResultado.resultado.equipe }}</h3>
              <p>Resultado agregado da equipe. Nenhuma resposta individual é exibida.</p>
            </div>
          </div>
          <div class="result-summary">
            <div>
              <span>Participações</span>
              <strong>{{ ultimoResultado.resultado.respostas }}</strong>
              <small>Mínimo para leitura: {{ ultimoResultado.resultado.minimoRespostas }}</small>
            </div>
            <div>
              <span>Média geral</span>
              <strong>{{ formatarNota(ultimoResultado.resultado.media) }}</strong>
              <b-tag :type="tipoFarol(ultimoResultado.resultado.farol)">{{ textoFarol(ultimoResultado.resultado.farol) }}</b-tag>
            </div>
          </div>
          <div class="result-dimensions">
            <div v-for="dimensao in ultimoResultado.resultado.dimensoes" :key="dimensao.id" class="result-dimension">
              <span>{{ dimensao.nome }}</span>
              <div class="result-track" :aria-label="`${dimensao.nome}: ${formatarNota(dimensao.media)} de 5`">
                <i :style="{ width: (Number(dimensao.media) / 5) * 100 + '%' }"></i>
              </div>
              <strong>{{ formatarNota(dimensao.media) }}</strong>
            </div>
          </div>
        </div>
        <div v-else-if="resultadoDisponivel && !loading" class="empty-state">
          Ainda não há resultado divulgado de um ciclo que você respondeu. Ele aparecerá aqui após o encerramento e o mínimo de participações.
        </div>
        <div v-else-if="!loading" class="empty-state">Não foi possível carregar seu último resultado agora.</div>
      </section>
    </template>
  </main>
</template>

<script>
import http from '@/http'
import RhCabecalho from '@/components/rh_cabecalho.vue'

export default {
  name: 'RhDashboardColaborador',
  components: { RhCabecalho },
  data() {
    return {
      empresas: [],
      unidadeId: null,
      equipes: [],
      ciclos: [],
      ultimoResultado: null,
      pdis: [],
      reunioes: [],
      metas: [],
      cursos: [],
      minimoRespostas: null,
      indicadoresDisponiveis: false,
      ciclosDisponiveis: false,
      resultadoDisponivel: false,
      loading: false,
      erro: '',
      requisicao: 0,
    }
  },
  computed: {
    primeiroNome() {
      const usuario = this.$store.state.usuario || {}
      const nome = usuario.apelido || usuario.funcionarioNome || usuario.nomeCadastro || usuario.nome || 'você'
      return String(nome).trim().split(/\s+/)[0]
    },
    equipePrincipal() {
      return this.equipes[0] || null
    },
    cicloDestaque() {
      return this.ciclos[0] || null
    },
    pdisAtivos() {
      return this.pdis.filter((pdi) => pdi.status !== 'concluido' && pdi.status !== 'cancelado').length
    },
    proximosEncontros() {
      return this.reunioes.filter((reuniao) =>
        reuniao.status !== 'cancelada' && reuniao.proximaData && new Date(reuniao.proximaData).getTime() >= Date.now()
      ).length
    },
    cursosConcluidos() {
      return this.cursos.filter((curso) => curso.status === 'CONCLUIDO').length
    },
  },
  created() {
    this.iniciar()
  },
  methods: {
    destinoCiclo(ciclo) {
      return { path: '/rh/diagnostico', query: { unidadeId: String(this.unidadeId), cicloId: String(ciclo.id), tipo: ciclo.tipo } }
    },
    dataCurta(valor) {
      return valor ? new Date(valor).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }) : '—'
    },
    formatarNota(valor) {
      return Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    tipoFarol(valor) {
      return valor === 'saudavel' ? 'is-success' : valor === 'atencao' ? 'is-warning' : 'is-danger'
    },
    textoFarol(valor) {
      return valor === 'saudavel' ? 'Saudável' : valor === 'atencao' ? 'Atenção' : 'Crítico'
    },
    selecionarEmpresa(id) {
      this.unidadeId = id
      this.carregar()
    },
    async iniciar() {
      this.loading = true
      try {
        this.empresas = (await http.get('/api/rh/estrutura/empresas')).data || []
        const atual = Number(this.$store.state.unidadeId)
        this.unidadeId = this.empresas.some((empresa) => empresa.id === atual)
          ? atual
          : (this.empresas[0] && this.empresas[0].id) || null
        if (this.unidadeId) await this.carregar()
      } catch (e) {
        this.erro = e && e.data && e.data.erro ? e.data.erro : 'Não foi possível carregar seu painel.'
      } finally {
        this.loading = false
      }
    },
    async carregar() {
      const requisicao = ++this.requisicao
      const unidadeId = this.unidadeId
      this.loading = true
      this.erro = ''
      this.equipes = []
      this.ciclos = []
      this.ultimoResultado = null
      this.pdis = []
      this.reunioes = []
      this.metas = []
      this.cursos = []
      this.minimoRespostas = null
      this.indicadoresDisponiveis = false
      this.ciclosDisponiveis = false
      this.resultadoDisponivel = false
      if (!unidadeId) {
        this.loading = false
        return
      }
      try {
        const params = { unidadeId }
        const [equipes, ciclos, cursos, politica, resultado] = await Promise.allSettled([
          http.get('/api/rh/estrutura/equipes', { params }),
          http.get('/api/rh/diagnostico/meus-ciclos', { params }),
          http.get('/api/ead-aprendizagem/meus-cursos'),
          http.get('/api/rh/diagnostico/minimo-respostas', { params }),
          http.get('/api/rh/diagnostico/meu-ultimo-resultado', { params }),
        ])
        if (requisicao !== this.requisicao) return
        if (equipes.status === 'fulfilled') this.equipes = equipes.value.data || []
        if (ciclos.status === 'fulfilled') {
          this.ciclos = ciclos.value.data || []
          this.ciclosDisponiveis = true
        }
        if (cursos.status === 'fulfilled') this.cursos = (cursos.value.data || []).filter((curso) => curso.status !== 'BLOQUEADO')
        if (politica.status === 'fulfilled') this.minimoRespostas = politica.value.data && politica.value.data.minimoRespostas
        if (resultado.status === 'fulfilled') {
          this.ultimoResultado = resultado.value.data
          this.resultadoDisponivel = true
        }
        if ([equipes, ciclos, cursos, politica, resultado].some((resposta) => resposta.status === 'rejected'))
          this.erro = 'Parte das informações do painel não pôde ser carregada. Tente atualizar a página.'

        if (equipes.status === 'fulfilled') {
          const consultas = this.equipes.flatMap((equipe) =>
            ['pdis', 'reunioes', 'metas'].map((tipo) =>
              http.get(`/api/rh/desenvolvimento/${tipo}`, { params: { unidadeId, equipeId: equipe.id } })
            )
          )
          const resultados = await Promise.allSettled(consultas)
          if (requisicao !== this.requisicao) return
          this.indicadoresDisponiveis = resultados.every((resultado) => resultado.status === 'fulfilled') && cursos.status === 'fulfilled'
          if (this.indicadoresDisponiveis) {
            const funcionarioId = Number(this.$store.state.usuario.funcionarioId)
            resultados.forEach((resultado, indice) => {
              const itens = resultado.value.data || []
              const tipo = ['pdis', 'reunioes', 'metas'][indice % 3]
              this[tipo].push(...itens.filter((item) => item.colaboradorId === funcionarioId))
            })
          } else if (!this.erro) {
            this.erro = 'Não foi possível carregar todos os indicadores do seu desenvolvimento.'
          }
        }
      } catch (e) {
        if (requisicao === this.requisicao)
          this.erro = e && e.data && e.data.erro ? e.data.erro : 'Não foi possível carregar seu painel.'
      } finally {
        if (requisicao === this.requisicao) this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.rh-colaborador-page { --navy: #0c193a; --blue: #087cf4; --ink: #17223c; background: #f7f8fc; color: var(--ink); font-family: Montserrat, Arial, sans-serif; min-height: 100vh; padding: 0 28px 64px; border-radius: 18px; }
.welcome { background: radial-gradient(circle at 88% 12%, #173b76 0, transparent 32%), linear-gradient(125deg, #0c193a, #10234e); color: #fff; border-radius: 0 0 30px 30px; padding: 38px 44px 42px; }
.welcome-context { color: #a9c8ff; font-size: 14px; font-weight: 600; margin: 0 0 8px; }
.welcome h1 { color: #fff; font-size: clamp(34px, 4vw, 56px); line-height: 1.1; letter-spacing: -.04em; margin: 0 0 12px; font-weight: 800; }
.welcome-spark { color: #55c7dd; }
.welcome-copy { color: #d8e6ff; font-size: 15px; margin: 0; }
.welcome-other-teams { color: #a9c8ff; font-size: 12px; margin: 12px 0 0; }
.featured-cycle { margin-top: 30px; background: rgba(19, 42, 87, .92); border: 1px solid #31517e; border-radius: 24px; padding: 28px 32px; max-width: 820px; box-shadow: 0 18px 34px rgba(0, 8, 28, .16); }
.featured-top, .featured-meta, .section-heading, .cycle-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.featured-kicker { color: #9abfff; font-size: 12px; font-weight: 800; letter-spacing: .11em; }
.anonymous-pill { color: #59d6c3; background: #173d57; border-radius: 30px; padding: 6px 12px; font-size: 12px; font-weight: 700; white-space: nowrap; }
.anonymous-pill .icon, .featured-meta .icon, .featured-action .icon, .featured-secondary .icon, .text-link .icon, .cycle-link .icon { vertical-align: middle; }
.featured-cycle h2 { color: #fff; font-size: clamp(23px, 3vw, 34px); line-height: 1.2; font-weight: 800; margin: 18px 0 9px; }
.featured-cycle p { color: #c9d9f7; margin: 0 0 20px; line-height: 1.6; }
.featured-meta { justify-content: flex-start; flex-wrap: wrap; color: #d9e9ff; font-size: 13px; margin-bottom: 24px; }
.featured-action { display: flex; align-items: center; justify-content: center; gap: 8px; background: var(--blue); color: #fff; border-radius: 14px; min-height: 52px; padding: 12px 18px; font-weight: 800; transition: background .2s, transform .2s; }
.featured-action:hover { background: #2991ff; color: #fff; transform: translateY(-2px); }
.featured-cycle-empty { max-width: 640px; }
.featured-secondary { color: #a8d8ff; font-weight: 700; }
.featured-secondary:hover { color: #fff; }
.privacy-note { display: flex; gap: 18px; align-items: flex-start; margin: 24px 0 36px; padding: 20px 24px; background: #e8f0ff; border: 1px solid #d6e5fc; border-radius: 20px; color: #173056; }
.privacy-icon { color: #236af3; padding-top: 3px; }
.privacy-note strong { font-size: 14px; }
.privacy-note p { font-size: 14px; line-height: 1.65; margin: 4px 0 0; }
.development-section, .cycles-section, .result-section { margin-top: 34px; }
.section-heading { align-items: end; margin-bottom: 20px; }
.eyebrow { color: #2879c6; font-size: 11px; font-weight: 800; letter-spacing: .16em; margin: 0 0 6px; }
.section-heading h2 { color: var(--navy); font-size: clamp(23px, 2.6vw, 32px); line-height: 1.2; font-weight: 800; margin: 0 0 5px; }
.section-heading p:last-child { color: #66748a; font-size: 14px; margin: 0; }
.text-link { color: #095fcc; font-size: 13px; font-weight: 800; white-space: nowrap; }
.text-link:hover, .cycle-link:hover { color: #0a4390; }
.indicator-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.indicator-card { display: flex; flex-direction: column; min-height: 186px; padding: 23px; background: #fff; border: 1px solid #e3e8f0; border-radius: 20px; box-shadow: 0 10px 24px rgba(16, 32, 68, .04); color: var(--ink); transition: transform .2s, box-shadow .2s; }
.indicator-card:hover { color: var(--ink); transform: translateY(-3px); box-shadow: 0 14px 30px rgba(16, 32, 68, .1); }
.indicator-icon { display: grid; place-items: center; width: 40px; height: 40px; border-radius: 12px; margin-bottom: auto; }
.indicator-icon.violet { color: #714be2; background: #efeaff; }
.indicator-icon.blue { color: #1b76d9; background: #e5f1ff; }
.indicator-icon.peach { color: #bd6a27; background: #fff0e1; }
.indicator-icon.mint { color: #168b7c; background: #ddf8f1; }
.indicator-value { color: var(--navy); font-size: 35px; font-weight: 800; line-height: 1.1; margin: 16px 0 6px; }
.indicator-card strong { font-size: 13px; }
.indicator-card small { color: #778398; font-size: 11px; margin-top: 4px; }
.cycle-count { color: #2b67b7; background: #e8f0ff; border-radius: 30px; font-size: 12px; font-weight: 800; padding: 8px 13px; white-space: nowrap; }
.cycle-list { display: grid; gap: 12px; }
.cycle-row { background: #fff; border: 1px solid #e3e8f0; border-radius: 17px; padding: 18px 22px; box-shadow: 0 8px 20px rgba(16, 32, 68, .035); }
.cycle-badge { display: grid; place-items: center; background: #eaf2ff; color: #1b75e5; border-radius: 12px; width: 44px; height: 44px; flex: none; }
.cycle-details { display: flex; flex-direction: column; gap: 4px; flex: 1; min-width: 0; }
.cycle-details strong { color: var(--navy); font-size: 15px; }
.cycle-details span { color: #718096; font-size: 12px; }
.cycle-link { color: #126fd3; font-size: 13px; font-weight: 800; white-space: nowrap; }
.result-card { background: #fff; border: 1px solid #e3e8f0; border-radius: 22px; padding: 30px; box-shadow: 0 10px 24px rgba(16, 32, 68, .04); }
.result-heading h3 { color: var(--navy); font-size: 25px; font-weight: 800; margin: 0 0 5px; }
.result-heading > div > p:last-child { color: #66748a; font-size: 13px; margin: 0; }
.result-summary { display: flex; flex-wrap: wrap; gap: 14px; margin: 24px 0 28px; }
.result-summary > div { display: flex; flex-direction: column; gap: 5px; min-width: 175px; background: #f6f8fc; border-radius: 15px; padding: 19px 22px; }
.result-summary span, .result-summary small { color: #6c7890; font-size: 12px; }
.result-summary strong { color: var(--navy); font-size: 31px; line-height: 1.2; }
.result-summary .tag { align-self: flex-start; }
.result-dimensions { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px 28px; }
.result-dimension { display: grid; grid-template-columns: minmax(120px, 175px) minmax(70px, 1fr) 42px; align-items: center; gap: 12px; min-width: 0; }
.result-dimension > span { color: var(--ink); font-size: 13px; }
.result-dimension strong { color: var(--navy); font-size: 13px; text-align: right; }
.result-track { height: 10px; background: #e7ebf1; border-radius: 30px; overflow: hidden; }
.result-track i { display: block; height: 100%; background: linear-gradient(90deg, #0e8fa8, #087cf4); border-radius: inherit; }
.empty-state { background: #fff; color: #66748a; border: 1px dashed #cfd8e6; border-radius: 18px; padding: 25px; text-align: center; font-size: 14px; }
@media (max-width: 1000px) { .indicator-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } .result-dimensions { grid-template-columns: 1fr; } }
@media (max-width: 600px) { .rh-colaborador-page { padding: 0 14px 36px; } .welcome { border-radius: 0 0 26px 26px; padding: 26px 21px 28px; } .featured-cycle { padding: 21px; margin-top: 24px; } .featured-top, .featured-meta, .section-heading { align-items: flex-start; } .section-heading { flex-direction: column; } .privacy-note { padding: 18px; margin-bottom: 28px; } .indicator-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; } .indicator-card { min-height: 165px; padding: 15px; } .indicator-value { font-size: 29px; } .cycle-row { flex-wrap: wrap; padding: 16px; } .cycle-link { margin-left: 60px; } .result-card { padding: 20px; } .result-dimension { grid-template-columns: minmax(84px, 1fr) minmax(68px, 1fr) 40px; gap: 8px; } }
@media (max-width: 380px) { .indicator-grid { grid-template-columns: 1fr; } }
</style>
